import { useEffect, useMemo, useRef, useState } from "react";
import { ExclamationTriangleIcon } from "@heroicons/react/20/solid";
import { AnnotationObb } from "./annotations/AnnotationObb";
import { useZoomPan } from "./annotations/useZoomPan";

const MIN_SIZE = 0.005;
const MIN_CREATE_DRAG_PX = 1;
const CM_PER_PIXEL = 0.0075;

const cloneAnnotations = (annotations) => {
  return annotations.map((annotation) => ({
    ...annotation,
    width: annotation.width ?? MIN_SIZE,
    height: annotation.height ?? MIN_SIZE,
    angle: annotation.angle ?? 0,
    class: annotation.class ?? 'other',
  }));
};

const generateAnnotationId = () => {
  return Math.random().toString(36).slice(2, 8);
};

const clamp = (value, min, max) => {
  return Math.min(Math.max(value, min), max);
};

const getAngleFromCenter = (center, point, imageWidth, imageHeight) => {
  const deltaX = (point.x - center.x) * imageWidth;
  const deltaY = (point.y - center.y) * imageHeight;

  return Math.atan2(-deltaY, deltaX) * 180 / Math.PI;
};

const getAngleDelta = (startAngle, currentAngle) => {
  let delta = currentAngle - startAngle;

  if (delta > 180) delta -= 360;
  if (delta < -180) delta += 360;

  return delta;
};

export const AnnotationImageEditor = ({ src, annotationData, disabled=false, onChange }) => {
  const imageRef = useRef(null);
  const svgRef = useRef(null);
  const interactionRef = useRef(null);

  const {
    viewportRef,
    stageRef,
    zoom,
    isPanning,
    isAltPressed,
    imageSize,
    canvasWidth,
    canvasHeight,
    stageLeft,
    stageTop,
    updateSizes,
    handlePointerEnter,
    handlePointerLeave,
    handlePanPointerDown,
    handlePanPointerMove,
    handlePanPointerUp,
  } = useZoomPan({ imageRef, src });

  const userAnnotations = annotationData?.user ?? [];
  const automaticAnnotations = annotationData?.auto ?? [];

  const initialAnnotations = useMemo(() => {
    return cloneAnnotations(userAnnotations.length > 0 ? userAnnotations : automaticAnnotations);
  }, [userAnnotations, automaticAnnotations]);

  const [annotations, setAnnotations] = useState(initialAnnotations);
  const [selectedAnnotationId, setSelectedAnnotationId] = useState(null);
  const [hoveredAnnotationId, setHoveredAnnotationId] = useState(null);
  const [hasImageError, setHasImageError] = useState(false);

  useEffect(() => {
    setAnnotations(initialAnnotations);
    setSelectedAnnotationId(null);
    setHoveredAnnotationId(null);
    interactionRef.current = null;
  }, [initialAnnotations]);

  useEffect(() => {
    const hasUnsavedChanges = JSON.stringify(annotations) !== JSON.stringify(initialAnnotations);
    onChange?.(annotations, hasUnsavedChanges);
  }, [annotations, initialAnnotations]);

  useEffect(() => {
    setHasImageError(false);
    interactionRef.current = null;
  }, [src]);

  const getPhysicalDimensions = (width, height) => {
    const naturalWidth = imageRef.current?.naturalWidth ?? 0;
    const naturalHeight = imageRef.current?.naturalHeight ?? 0;

    const widthCm = width * naturalWidth * CM_PER_PIXEL;
    const heightCm = height * naturalHeight * CM_PER_PIXEL;

    return {
      width_cm: widthCm,
      height_cm: heightCm,
      area_cm2: widthCm * heightCm,
    };
  };

  const getNormalizedPoint = (event) => {
    if (!svgRef.current) return null;

    const rect = svgRef.current.getBoundingClientRect();

    return {
      x: (event.clientX - rect.left) / rect.width,
      y: (event.clientY - rect.top) / rect.height,
    };
  };

  const updateAnnotation = (annotationId, updateFunction) => {
    setAnnotations((currentAnnotations) => currentAnnotations.map((annotation) => {
      if (annotation.id !== annotationId) return annotation;
      return updateFunction(annotation);
    }));
  };

  const handleClassChange = (annotationId, defectClass) => {
    updateAnnotation(annotationId, (annotation) => ({
      ...annotation,
      class: defectClass,
    }));
  };

  const handlePointerDown = (event) => {
    if (disabled || event.altKey || event.target !== svgRef.current) return;

    event.preventDefault();

    const point = getNormalizedPoint(event);
    if (!point) return;

    const startPoint = {
      x: clamp(point.x, 0, 1),
      y: clamp(point.y, 0, 1),
    };

    svgRef.current.setPointerCapture(event.pointerId);

    const annotation = {
      id: generateAnnotationId(),
      x: startPoint.x,
      y: startPoint.y,
      width: MIN_SIZE,
      height: MIN_SIZE,
      angle: 0,
      class: 'other',
      ...getPhysicalDimensions(MIN_SIZE, MIN_SIZE),
    };

    setAnnotations((currentAnnotations) => [...currentAnnotations, annotation]);
    setSelectedAnnotationId(annotation.id);

    interactionRef.current = {
      type: 'create',
      annotationId: annotation.id,
      startPoint,
      startClientX: event.clientX,
      startClientY: event.clientY,
    };
  };

  const handleObbPointerDown = (event, annotation, interactionType, handle=null) => {
    if (disabled || event.altKey) return;

    event.preventDefault();
    event.stopPropagation();

    const point = getNormalizedPoint(event);
    if (!point) return;

    svgRef.current?.setPointerCapture(event.pointerId);
    setSelectedAnnotationId(annotation.id);

    interactionRef.current = {
      type: interactionType,
      handle,
      annotationId: annotation.id,
      startPoint: point,
      initialAnnotation: { ...annotation },
    };
  };

  const handleRotatePointerDown = (event, annotation) => {
    if (disabled || event.altKey || !imageSize) return;

    event.preventDefault();
    event.stopPropagation();

    const point = getNormalizedPoint(event);
    if (!point) return;

    svgRef.current?.setPointerCapture(event.pointerId);
    setSelectedAnnotationId(annotation.id);

    interactionRef.current = {
      type: 'rotate',
      annotationId: annotation.id,
      initialAnnotation: { ...annotation },
      startPointerAngle: getAngleFromCenter(annotation, point, imageSize.width, imageSize.height),
    };
  };

  const resizeAnnotation = (interaction, point) => {
    const annotation = interaction.initialAnnotation;
    const angleRadians = annotation.angle * Math.PI / 180;

    const centerX = annotation.x * imageSize.width;
    const centerY = annotation.y * imageSize.height;
    const pointerX = point.x * imageSize.width;
    const pointerY = point.y * imageSize.height;

    const deltaX = pointerX - centerX;
    const deltaY = pointerY - centerY;

    const localX = deltaX * Math.cos(angleRadians) - deltaY * Math.sin(angleRadians);
    const localY = deltaX * Math.sin(angleRadians) + deltaY * Math.cos(angleRadians);

    let left = -annotation.width * imageSize.width / 2;
    let right = annotation.width * imageSize.width / 2;
    let top = -annotation.height * imageSize.height / 2;
    let bottom = annotation.height * imageSize.height / 2;

    const minWidth = MIN_SIZE * imageSize.width;
    const minHeight = MIN_SIZE * imageSize.height;

    if (interaction.handle === 'w') left = Math.min(localX, right - minWidth);
    if (interaction.handle === 'e') right = Math.max(localX, left + minWidth);
    if (interaction.handle === 'n') top = Math.min(localY, bottom - minHeight);
    if (interaction.handle === 's') bottom = Math.max(localY, top + minHeight);

    const localCenterX = (left + right) / 2;
    const localCenterY = (top + bottom) / 2;

    const globalCenterX = localCenterX * Math.cos(angleRadians) + localCenterY * Math.sin(angleRadians);
    const globalCenterY = -localCenterX * Math.sin(angleRadians) + localCenterY * Math.cos(angleRadians);

    const width = Math.max((right - left) / imageSize.width, MIN_SIZE);
    const height = Math.max((bottom - top) / imageSize.height, MIN_SIZE);

    return {
      ...annotation,
      x: annotation.x + globalCenterX / imageSize.width,
      y: annotation.y + globalCenterY / imageSize.height,
      width,
      height,
      ...getPhysicalDimensions(width, height),
    };
  };

  const handlePointerMove = (event) => {
    if (disabled || !interactionRef.current) return;

    event.preventDefault();

    const point = getNormalizedPoint(event);
    if (!point) return;

    const interaction = interactionRef.current;

    if (interaction.type === 'create') {
      const left = Math.min(interaction.startPoint.x, point.x);
      const right = Math.max(interaction.startPoint.x, point.x);
      const top = Math.min(interaction.startPoint.y, point.y);
      const bottom = Math.max(interaction.startPoint.y, point.y);

      const width = Math.max(right - left, MIN_SIZE);
      const height = Math.max(bottom - top, MIN_SIZE);

      updateAnnotation(interaction.annotationId, (annotation) => ({
        ...annotation,
        x: (left + right) / 2,
        y: (top + bottom) / 2,
        width,
        height,
        ...getPhysicalDimensions(width, height),
      }));

      return;
    }

    if (interaction.type === 'move') {
      const deltaX = point.x - interaction.startPoint.x;
      const deltaY = point.y - interaction.startPoint.y;
      const initialAnnotation = interaction.initialAnnotation;

      updateAnnotation(interaction.annotationId, (annotation) => ({
        ...annotation,
        x: clamp(initialAnnotation.x + deltaX, 0, 1),
        y: clamp(initialAnnotation.y + deltaY, 0, 1),
      }));

      return;
    }

    if (interaction.type === 'rotate') {
      const currentPointerAngle = getAngleFromCenter(interaction.initialAnnotation, point, imageSize.width, imageSize.height);
      const deltaAngle = getAngleDelta(interaction.startPointerAngle, currentPointerAngle);

      updateAnnotation(interaction.annotationId, (annotation) => ({
        ...annotation,
        angle: interaction.initialAnnotation.angle + deltaAngle,
      }));

      return;
    }

    if (interaction.type === 'resize') updateAnnotation(interaction.annotationId, () => resizeAnnotation(interaction, point));
  };

  const handlePointerUp = (event) => {
    event.preventDefault();

    const interaction = interactionRef.current;

    if (interaction?.type === 'create') {
      const deltaX = event.clientX - interaction.startClientX;
      const deltaY = event.clientY - interaction.startClientY;
      const dragDistance = Math.hypot(deltaX, deltaY);

      if (dragDistance < MIN_CREATE_DRAG_PX) {
        setAnnotations((currentAnnotations) => currentAnnotations.filter((annotation) => annotation.id !== interaction.annotationId));

        if (selectedAnnotationId === interaction.annotationId) setSelectedAnnotationId(null);
      }
    }

    if (svgRef.current?.hasPointerCapture(event.pointerId)) svgRef.current.releasePointerCapture(event.pointerId);

    interactionRef.current = null;
  };

  const handleDeleteAnnotation = (event, annotationId) => {
    if (disabled) return;

    event.preventDefault();
    event.stopPropagation();

    setAnnotations((currentAnnotations) => currentAnnotations.filter((annotation) => annotation.id !== annotationId));

    if (selectedAnnotationId === annotationId) setSelectedAnnotationId(null);
  };

  const handleImageError = () => {
    setHasImageError(true);
    interactionRef.current = null;
  };

  return (
    <div ref={viewportRef}
         className={`relative h-full min-h-0 ${zoom > 1 ? 'overflow-auto' : 'overflow-hidden'} bg-perception-black-700 rounded-md select-none ${
           isPanning ? 'cursor-grabbing' : isAltPressed ? 'cursor-grab' : ''
         }`}
         onPointerEnter={handlePointerEnter} onPointerLeave={handlePointerLeave}
         onPointerDown={handlePanPointerDown} onPointerMove={handlePanPointerMove} onPointerUp={handlePanPointerUp} onPointerCancel={handlePanPointerUp}>
      {hasImageError ? (
        <div className="h-full w-full flex flex-col items-center justify-center gap-3 p-6 text-center">
          <ExclamationTriangleIcon className="size-12 text-perception-yellow" />

          <div>
            <div className="font-semibold text-white">Image originale indisponible</div>
            <div className="mt-1 text-sm text-perception-gray-500">Le fichier est absent ou n’a pas pu être chargé.</div>
          </div>
        </div>
      ) : (
        <>
          <img ref={imageRef} src={src} alt="" className="absolute invisible pointer-events-none" onLoad={updateSizes} onError={handleImageError} draggable={false} />

          {imageSize && (
            <div className="relative" style={{ width: canvasWidth, height: canvasHeight }}>
              <div ref={stageRef} className="absolute" style={{ left: stageLeft, top: stageTop, width: imageSize.width, height: imageSize.height }}>
                <img src={src} alt="Photo à annoter" className="absolute inset-0 h-full w-full select-none pointer-events-none" draggable={false} />

                <svg ref={svgRef}
                     className={`absolute inset-0 h-full w-full select-none ${disabled ? 'cursor-not-allowed' : isPanning ? 'cursor-grabbing' : isAltPressed ? 'cursor-grab' : 'cursor-crosshair'}`}
                     style={{ touchAction: 'none', pointerEvents: isAltPressed ? 'none' : 'auto' }}
                     onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={handlePointerUp} onPointerCancel={handlePointerUp}
                     onDragStart={(event) => event.preventDefault()} onClick={(event) => event.target === svgRef.current && setSelectedAnnotationId(null)}>
                  {annotations.map((annotation) => (
                    <AnnotationObb key={annotation.id} annotation={annotation} imageWidth={imageSize.width} imageHeight={imageSize.height}
                                   selected={selectedAnnotationId === annotation.id} hovered={hoveredAnnotationId === annotation.id} disabled={disabled}
                                   onMouseEnter={() => setHoveredAnnotationId(annotation.id)} onMouseLeave={() => setHoveredAnnotationId(null)}
                                   onMoveStart={(event) => handleObbPointerDown(event, annotation, 'move')}
                                   onResizeStart={(event, handle) => handleObbPointerDown(event, annotation, 'resize', handle)}
                                   onRotateStart={(event) => handleRotatePointerDown(event, annotation)}
                                   onClassChange={(defectClass) => handleClassChange(annotation.id, defectClass)}
                                   onDelete={(event) => handleDeleteAnnotation(event, annotation.id)} />
                  ))}
                </svg>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};